/**
 * PlanTermCard.tsx — ONE term's price block, shared patient-side and admin-side.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C2.1 (plan builder redo).
 *
 * WHY shared: the patient plan selector (checkout/PlanSelection) and the admin
 * plan-builder's live "How patients will see it" preview (admin/PlanManagement)
 * must render a term's price IDENTICALLY. One renderer means the preview can
 * never drift from what patients actually see.
 *
 * PRICE HERO = THE MONTHLY FIGURE (2026-08-14, Ashley Haveloch-Jones / Ali Abbas
 * Jaffari). It used to be the TERM TOTAL, so a 12-month plan opened with
 * "£2,032.80" — the number that loses the sale before anyone reads what they
 * get. What a patient decides is "can I afford this each month". The total is
 * never hidden (it is the real commitment, and burying it would be a dark
 * pattern) — it just stops being the loudest thing on the card.
 *
 * NO RESERVED BLANK ROWS ANY MORE (2026-09-26, the CTO: "the cards where we show
 * the prices are a bit too large … reduce the card height, even on web"). Every
 * row used to reserve its height even when empty (2026-08-14), from the days a
 * term card could carry a badge above its price. The badges went on 2026-08-28,
 * so the price is always the card's second line and lines up across the row by
 * itself; the only optional row, the "was" + saving pair, is the LAST one, and
 * the grid gives every card in a row the same height (`height: 100%`). The
 * reserves had become blank space on the 1-month card and nothing else.
 *
 * Accessibility: when a saving applies the whole cluster carries a SINGLE
 * combined `aria-label`, and the visible pieces are `aria-hidden` — a screen
 * reader hears one natural sentence, not six fragments. The save cues carry
 * their meaning in words, never colour alone.
 */
import type { PricePlan } from '../api/types';
import { formatMonthly, formatPence } from '../lib/format';
import type { TermPricing } from '../lib/termPricing';

/**
 * The saving cue, legible on BOTH card surfaces.
 *
 * The shared `Badge tone="positive"` is light-green on light-green, so it
 * dissolved into the SELECTED term card — the discount disappeared from exactly
 * the card the patient had just chosen (caught at 375px, 2026-08-14). A white
 * fill with a green rule holds its edge against the white unselected card and
 * the tinted selected one alike. The wording carries the meaning; colour only
 * reinforces it.
 */
function SaveTag({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        background: 'var(--gps-surface)',
        border: '1px solid var(--gps-primary-tint-strong)',
        color: 'var(--gps-green-text)',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1.4,
        padding: '1px 6px',
        borderRadius: 'var(--gps-r-pill)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function PlanTermCard({ plan, pricing }: { plan: PricePlan; pricing: TermPricing }) {
  if (plan.monthlyPence <= 0) {
    return (
      <span style={{ display: 'block', fontSize: '13px', color: 'var(--gps-muted)', marginTop: '2px' }}>
        {formatMonthly(plan.monthlyPence)}
      </span>
    );
  }

  const hasSaving = pricing.savePence > 0;
  // The undiscounted MONTHLY rate. `fullPence` is the term total at the 1-month
  // reference rate, so dividing by the term gives the "was £X/month" a patient
  // compares against. `Math.round` only at this DISPLAY boundary — never fed
  // back into anything that charges.
  const monthlyFullPence = Math.round(pricing.fullPence / plan.months);
  const savePercent = Math.round((pricing.savePence / pricing.fullPence) * 100);
  const rowLabel = hasSaving
    ? `Was ${formatPence(monthlyFullPence)} a month, now ${formatPence(plan.monthlyPence)} a month, saving ${formatPence(pricing.savePence)}, ${savePercent} percent off, ${formatPence(pricing.actualPence)} total over ${plan.months} months`
    : `${formatPence(plan.monthlyPence)} a month, ${formatPence(pricing.actualPence)} total over ${plan.months} month${plan.months === 1 ? '' : 's'}`;

  return (
    <span style={{ display: 'block' }}>
      {/* The whole block reads as ONE sentence to a screen reader — including
          the term total, which the visual card no longer shows. Delivered as a
          visually-hidden span rather than an `aria-label` on this wrapper: ARIA
          prohibits `aria-label` on a generic <span> with no role, and axe fails
          it (caught by the admin preview's a11y scan, 2026-08-14). The visible
          pieces below are each `aria-hidden` so nothing is announced twice. */}
      <span className="gps-sr-only">{rowLabel}</span>
      {/* ROW: price — one line, always (`nowrap`). Display face at 600: main.tsx
          bundles Fraunces at 400 and 600 only, so the 800 this used to ask for
          was a synthesised bold (the order summaries share the same rule). */}
      <span aria-hidden="true" style={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap' }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: 1.15,
            color: 'var(--gps-text)',
            fontFamily: 'var(--gps-font-display)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatPence(plan.monthlyPence)}
        </span>
        <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)', marginLeft: '2px', fontWeight: 600 }}>/month</span>
      </span>

      {/* ROW: the struck "was" rate and the cash saving, TOGETHER on one compact
          row that wraps when the card is too narrow (2026-09-26, the CTO: "reduce
          the card height"). The "was" figure sits directly under the per-month
          price it is the reference for, so it drops the repeated "/month" (the
          screen-reader sentence above still says "a month"); that is what lets
          the pair share one line in a phone's two-column grid. Cash saving only
          (2026-08-14, CTO): the percentage made a real £17 read as trivial. No
          saving ⇒ no row — never an invented one. */}
      {hasSaving ? (
        <span
          aria-hidden="true"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '3px 5px',
            marginTop: '4px',
            fontSize: '11.5px',
            lineHeight: 1.3,
            color: 'var(--gps-muted)',
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>
            was <s>{formatPence(monthlyFullPence)}</s>
          </span>
          <SaveTag>Save {formatPence(pricing.savePence)}</SaveTag>
        </span>
      ) : null}

      {/* The term total was REMOVED from this card (2026-08-14, CTO): the card
          answers "what does this cost me a month", and the total is the number
          that stalls that decision. It is NOT lost — the order summary states
          the full commitment ("N-month total, billed monthly", `OrderSummary`
          `termTotalLabel`), and the agreement panel restates it above the point
          that says the plan runs for the whole term, which is where a patient
          actually needs it. The screen must never let someone buy without
          seeing it. */}
    </span>
  );
}
